import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Download, LogOut, RefreshCw, Inbox } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "Site Visit Scheduled", "Closed"];

const fmtErr = (d) =>
  typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg || "").filter(Boolean).join(" ") : "Something went wrong";

const inputCls =
  "w-full bg-[#0B0C0E] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-[#F3F4F6] placeholder:text-[#4B5563] focus:outline-none focus:border-[#D4AF37]/60 focus:ring-1 focus:ring-[#D4AF37]/30 transition-all duration-300";

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onSuccess(data);
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E] flex items-center justify-center px-5">
      <motion.form
        data-testid="admin-login-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm rounded-3xl border border-white/[0.07] bg-[#121418] p-8"
      >
        <div className="flex items-center gap-3 mb-8">
          <LogoMark size={40} />
          <div>
            <div className="font-display text-lg font-semibold tracking-[0.25em] text-[#F3F4F6]">EON FAIRFOX</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF]">Investor Relations Console</div>
          </div>
        </div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mb-2">Email</label>
        <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@eonfairfox.com" className={inputCls} />
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mt-5 mb-2">Password</label>
        <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
        {error && (
          <p data-testid="admin-login-error" className="mt-4 text-xs text-red-400">{error}</p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit-btn"
          disabled={loading}
          className="mt-7 w-full py-3.5 rounded-full bg-[#D4AF37] text-[#0B0C0E] font-sans text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F3E5AB] disabled:opacity-50 transition-all duration-300"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
        <Link to="/" data-testid="admin-back-link" className="block mt-5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B7280] hover:text-[#D4AF37] transition-colors">
          ← Back to site
        </Link>
      </motion.form>
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch (err) {
      if (err.response?.status === 401) setAuth(false);
      else toast.error("Could not load enquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setAuth(data))
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth) fetchEnquiries();
  }, [auth, fetchEnquiries]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/enquiries/${id}`, { status }, { withCredentials: true });
      setEnquiries((list) => list.map((e) => (e.id === id ? data : e)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setAuth(false);
  };

  const exportCsv = () => {
    const header = ["Date", "Name", "Email", "Phone", "Unit Type", "Budget", "Visit Date", "Status", "Message"];
    const rows = filtered.map((e) => [
      new Date(e.created_at).toLocaleString("en-IN"),
      e.name, e.email, e.phone, e.unit_type, e.budget || "", e.visit_date || "", e.status,
      (e.message || "").replace(/[\n\r,]+/g, " "),
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "eon-fairfox-enquiries.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return enquiries.filter(
      (e) =>
        (statusFilter === "All" || e.status === statusFilter) &&
        (!q || [e.name, e.email, e.phone, e.unit_type].some((v) => v?.toLowerCase().includes(q)))
    );
  }, [enquiries, search, statusFilter]);

  if (auth === null) {
    return (
      <div className="min-h-screen bg-[#0B0C0E] flex items-center justify-center" data-testid="admin-loading">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#9CA3AF] animate-pulse">Verifying session…</span>
      </div>
    );
  }
  if (auth === false) return <LoginForm onSuccess={setAuth} />;

  const metrics = [
    { label: "Total Enquiries", value: enquiries.length },
    { label: "New", value: enquiries.filter((e) => e.status === "New").length },
    { label: "Visits Scheduled", value: enquiries.filter((e) => e.status === "Site Visit Scheduled").length },
    { label: "Closed", value: enquiries.filter((e) => e.status === "Closed").length },
  ];

  return (
    <div className="min-h-screen bg-[#0B0C0E] text-[#F3F4F6] font-body">
      <header className="border-b border-white/[0.07] bg-[#0B0C0E]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={32} />
            <span className="font-display text-lg font-semibold tracking-[0.2em]">ENQUIRIES</span>
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] ml-2">
              {auth.email}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-export-btn" onClick={exportCsv} className="px-4 py-2 rounded-full border border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-[#F3E5AB] hover:border-[#D4AF37]/50 transition-all flex items-center gap-2">
              <Download size={13} /> Export CSV
            </button>
            <button data-testid="admin-refresh-btn" onClick={fetchEnquiries} className="p-2.5 rounded-full border border-white/10 text-[#9CA3AF] hover:text-[#F3E5AB] hover:border-[#D4AF37]/50 transition-all">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="px-4 py-2 rounded-full bg-[#D4AF37] text-[#0B0C0E] font-mono text-[10px] font-semibold uppercase tracking-[0.2em] hover:bg-[#F3E5AB] transition-all flex items-center gap-2">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-metrics">
          {metrics.map((m) => (
            <div key={m.label} className="rounded-2xl border border-white/[0.07] bg-[#121418] p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF]">{m.label}</div>
              <div className="mt-3 font-display text-3xl font-semibold gold-text">{m.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, unit…"
            className={`${inputCls} sm:max-w-sm`}
          />
          <select
            data-testid="admin-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`${inputCls} eon-select sm:max-w-[220px]`}
          >
            {["All", ...STATUSES].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="mt-6 rounded-2xl border border-white/[0.07] bg-[#121418] overflow-hidden" data-testid="admin-enquiries-table">
          {filtered.length === 0 ? (
            <div className="py-20 flex flex-col items-center gap-4 text-[#6B7280]" data-testid="admin-empty-state">
              <Inbox size={32} strokeWidth={1.5} />
              <p className="font-mono text-xs uppercase tracking-[0.25em]">
                {enquiries.length === 0 ? "No enquiries yet" : "Nothing matches your filters"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="border-b border-white/[0.07] text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">
                    <th className="px-6 py-4">Received</th>
                    <th className="px-6 py-4">Client</th>
                    <th className="px-6 py-4">Interest</th>
                    <th className="px-6 py-4">Visit</th>
                    <th className="px-6 py-4">Message</th>
                    <th className="px-6 py-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <tr key={e.id} data-testid={`enquiry-row-${e.id}`} className="border-b border-white/[0.05] hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4 font-mono text-xs text-[#9CA3AF] whitespace-nowrap">
                        {new Date(e.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                        <span className="block text-[10px] text-[#4B5563]">{new Date(e.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#F3F4F6]">{e.name}</div>
                        <div className="text-xs text-[#9CA3AF] mt-0.5">{e.email || "—"}</div>
                        <div className="text-xs text-[#9CA3AF]">{e.phone}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-[#F3F4F6]">{e.unit_type}</div>
                        {e.budget && <div className="text-xs text-[#D4AF37] mt-0.5">{e.budget}</div>}
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-[#9CA3AF]">{e.visit_date || "—"}</td>
                      <td className="px-6 py-4 text-xs text-[#9CA3AF] max-w-[220px] truncate" title={e.message || ""}>{e.message || "—"}</td>
                      <td className="px-6 py-4">
                        <select
                          data-testid={`status-select-${e.id}`}
                          value={e.status}
                          onChange={(ev) => updateStatus(e.id, ev.target.value)}
                          className={`eon-select bg-[#0B0C0E] border rounded-full px-3.5 py-2 text-xs font-mono focus:outline-none transition-colors ${
                            e.status === "New"
                              ? "border-[#D4AF37]/50 text-[#F3E5AB]"
                              : e.status === "Closed"
                              ? "border-white/10 text-[#6B7280]"
                              : "border-white/20 text-[#F3F4F6]"
                          }`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
